import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Button } from '@mui/material';
import { AddBoxIcon } from '@sharedCR/coreDesignSystem/icons';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import { entitySideDataTableStyles } from 'app/shared/componentsRestyled/common/form/shared/styles';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { ImpoundCategoryType } from 'constants/impoundTypes';
import useSort from 'hooks/useSort';
import { getConditionalObject, getOptionTypeName } from 'utils/data';
import { filterUsedOptions } from 'utils/loanApplication';

import {
  selectExistingImpoundTypesOptionsByCategory,
  selectImpoundTypesOptionsByCategory,
} from 'store/system/selectors';

import { PropertyAndLoanTestIds } from '../../../../../PropertyAndLoan.testIds';
import { tableWrapperStyles } from '../../../Content.styles';
import { getNotFoundTableData, prepareModifiedFields } from '../../../Content.utils';
import { pagesPropTypes } from '../../MainContent.constants';
import { propertyExpensesModifiedFieldsToExclude } from '../PropertyExpenses.constants';
import { createNewTableData, getValuesToSave } from '../PropertyExpenses.utils';

import { getTableConfig } from './PropertyTaxes.config';
import { defaultSortOrder, PropertyTaxesInfo, TableColumnId } from './PropertyTaxes.constants';
import PropertyTaxesForm from './PropertyTaxesForm';

const defaultObject = {};

const defaultPendingChanges = {};
const defaultOptions = [];

const PropertyTaxes = ({
  isLoading,
  form = defaultObject,
  propertyTaxes: propertyTaxesTableData = null,
  setEntitySideData,
  setInitialEntitySideData,
  entitySideData,
  initialEntitySideData = defaultObject,
  setIsSavingAllowed,
  pendingChanges,
  setIsNestedEntityEditActive,
}) => {
  const [editableData, setEditableData] = useState(null);

  const { order, onRequestSort } = useSort(defaultSortOrder);

  const impoundTypeOptions = useSelector(selectImpoundTypesOptionsByCategory);
  const existingImpoundTypeOptions = useSelector(selectExistingImpoundTypesOptionsByCategory);

  const propertyTaxTypeOptions =
    impoundTypeOptions[ImpoundCategoryType.PROPERTY_TAXES] || defaultOptions;
  const existingPropertyTaxTypeOptions =
    existingImpoundTypeOptions[ImpoundCategoryType.PROPERTY_TAXES] || defaultOptions;

  const isFormDataEmpty = _.isEmpty(form);
  const entityData = isFormDataEmpty ? propertyTaxesTableData : form;
  const isDataEmpty = _.isEmpty(entityData);
  const isDataLoading = isLoading && isDataEmpty;
  const { propertyTaxes: propertyTaxesData } = entitySideData;
  const propertyTaxes = propertyTaxesData?.map(tax => ({
    ...tax,
    name: getOptionTypeName(propertyTaxTypeOptions, tax.impoundTypeId),
    value: tax.impoundTypeId,
  }));

  const filteredPropertyTaxTypeOptions = filterUsedOptions({
    data: propertyTaxes,
    options: existingPropertyTaxTypeOptions,
    currentOption: editableData?.impoundTypeId,
  });

  const isEditActive = !!editableData;
  const isPropertyTaxesExist = !!propertyTaxes?.length;
  const pendingChangesData = pendingChanges?.propertyTaxesPendingChanges || defaultPendingChanges;

  useEffect(() => {
    setIsSavingAllowed(!isEditActive);
    setIsNestedEntityEditActive(isEditActive);

    return () => {
      setIsSavingAllowed(true);
    };
  }, [isEditActive, setIsSavingAllowed, setIsNestedEntityEditActive]);

  const propertyTaxesInitialValuesMap = useMemo(() => {
    return initialEntitySideData.propertyTaxes
      ? _.keyBy(initialEntitySideData.propertyTaxes, 'id')
      : {};
  }, [initialEntitySideData]);

  const orderedPropertyTaxesWithPendingChanges = useMemo(() => {
    const orderedPropertyTaxes = _.orderBy(propertyTaxes, [order.id], [order.direction]);

    return prepareModifiedFields({
      currentData: orderedPropertyTaxes,
      pendingChangesData,
      initialValuesMap: propertyTaxesInitialValuesMap,
      fieldsToExclude: propertyExpensesModifiedFieldsToExclude,
    });
  }, [order.direction, order.id, pendingChangesData, propertyTaxes, propertyTaxesInitialValuesMap]);

  const correctedPropertyTaxesTableData = isPropertyTaxesExist
    ? orderedPropertyTaxesWithPendingChanges
    : getNotFoundTableData(TableColumnId.IMPOUND_TYPE_ID);

  useEffect(() => {
    if (!propertyTaxesTableData) {
      return;
    }

    const isPropertyTaxesExist = _.has(entitySideData, PropertyTaxesInfo.KEY);

    if (isPropertyTaxesExist) {
      return;
    }

    const dataToSet = {
      ...getConditionalObject(!isPropertyTaxesExist, {
        propertyTaxes: propertyTaxesTableData,
      }),
    };

    setEntitySideData(prevState => ({
      ...prevState,
      ...dataToSet,
    }));
    setInitialEntitySideData(prevState => ({
      ...prevState,
      ...dataToSet,
    }));
  }, [entitySideData, propertyTaxesTableData, setEntitySideData, setInitialEntitySideData]);

  const deleteTableData = useCallback(
    id => {
      setEntitySideData(prevState => ({
        ...prevState,
        propertyTaxes: prevState?.propertyTaxes.reduce((acc, entity) => {
          if (entity.id !== id) {
            acc.push(entity);

            return acc;
          }

          if (entity.isAdded) {
            return acc;
          }

          acc.push({ ...entity, isDeleted: true });

          return acc;
        }, []),
      }));

      setEditableData(null);
    },
    [setEntitySideData],
  );

  const tableConfig = getTableConfig({
    propertyTaxTypeOptions,
    setEditableData,
    deleteTableData,
    editableRowId: editableData?.id,
    isActionDisabled: isLoading,
  });

  const addNewTableEntity = useCallback(() => {
    const entityData = createNewTableData();

    setEntitySideData(prevState => ({
      ...prevState,
      propertyTaxes: [...(prevState?.propertyTaxes ?? []), entityData],
    }));

    setEditableData(entityData);
  }, [setEntitySideData]);

  const cancelForm = useCallback(
    data => {
      if (data.isNewlyAdded) {
        setEntitySideData(prev => ({
          ...prev,
          propertyTaxes: prev.propertyTaxes.filter(({ id }) => id !== data.id),
        }));
      }

      setEditableData(null);
    },
    [setEntitySideData],
  );

  const saveForm = useCallback(
    ({ updatedData, initialData }) => {
      const dataToSave = getValuesToSave({ updatedData, initialData });

      setEntitySideData(prevState => ({
        ...prevState,
        propertyTaxes: prevState?.propertyTaxes?.map(propertyTax =>
          propertyTax.id === dataToSave.id ? dataToSave : propertyTax,
        ),
      }));

      setEditableData(null);
    },
    [setEntitySideData],
  );

  const additionalRowContent = editableData ? (
    <PropertyTaxesForm
      cancelForm={cancelForm}
      data={editableData}
      deleteTableData={deleteTableData}
      isLoading={isLoading}
      pendingChanges={pendingChangesData[editableData.id]}
      propertyTaxTypeOptions={filteredPropertyTaxTypeOptions}
      saveForm={saveForm}
    />
  ) : null;

  return (
    <>
      <TableCDS
        isHideTableWhenEmpty
        additionalRowContent={additionalRowContent}
        additionalRowContentRowId={editableData?.id}
        config={tableConfig}
        customStyles={entitySideDataTableStyles}
        data={correctedPropertyTaxesTableData}
        isCustomDataDefinition={!isPropertyTaxesExist}
        isLoading={isDataLoading}
        isSortDisabled={isEditActive}
        order={order}
        pendingChangesTextSelector="span"
        title={PropertyTaxesInfo.TITLE}
        wrapperStyle={tableWrapperStyles}
        onRequestSort={onRequestSort}
      />
      {!isLoading && (
        <div className="mt-24 pb-24">
          <Button
            data-testid={PropertyAndLoanTestIds.PROPERTY_TAXES_ADD_BUTTON}
            disabled={isEditActive || isLoading}
            size="medium"
            startIcon={<AddBoxIcon size={IconSize[20]} />}
            variant="outlined"
            onClick={addNewTableEntity}
          >
            Add {PropertyTaxesInfo.TITLE}
          </Button>
        </div>
      )}
    </>
  );
};

const usedProps = [
  'isLoading',
  'form',
  'setEntitySideData',
  'setInitialEntitySideData',
  'entitySideData',
  'initialEntitySideData',
  'setIsSavingAllowed',
  'pendingChanges',
  'setIsNestedEntityEditActive',
];

PropertyTaxes.propTypes = {
  ..._.pick(pagesPropTypes, usedProps),
  propertyTaxes: PropTypes.array,
};

export default PropertyTaxes;
